/**
 * The Fandom search pop-up (snippets/fd-search-modal.liquid): `<fd-search>` inside Horizon's
 * `<dialog-component id="search-modal">` (assets/dialog.js opens and closes it, with Escape, a click outside, the
 * scroll lock and focus return).
 *
 * - `data-state` on the element (`idle` | `results` | `empty`) picks what the body shows; the idle state (recent
 *   searches, Shop by team, Trending now) is rendered by Liquid, except the recent list.
 * - Recent searches live in localStorage (`fd-search-recent`: a JSON array of strings, newest first, at most 4,
 *   trimmed, at most 80 characters each, no case-insensitive duplicates). Storage may be blocked or hold garbage:
 *   every read and write is guarded, and the block stays hidden. Rows are built with textContent (never as HTML).
 * - The Clear button empties the box; the close button closes the dialog; Enter with an empty box does nothing.
 * - Chips, recent rows, suggestions (any `button[data-q]` inside) run their search in the pop-up and are remembered.
 *
 * Live results: typing runs the search after 250ms of quiet. `run(query)` fetches sections/fd-search-results.liquid
 * from Shopify's predictive search (`data-results-url`), or from the store search (`data-search-url`) where
 * `data-predictive="false"` (languages predictive search does not support, such as Arabic). Each run aborts the
 * previous one's requests, and a response for a query that is no longer the current one is dropped. The section's root
 * goes into `.fd-search__results`, its chips template replaces the header chips (the popular ones come back when it is
 * empty) and its `data-state` goes to the element. The real result count comes from a second, light request
 * (sections/fd-search-count.liquid; a store-search answer carries it as `data-count`) and fills "N products" and
 * "View all N results" through the translated patterns in `data-count-one`, `data-count-other` and
 * `data-view-all-count` (`[count]` stands for the number). The live region speaks once per search: the real count, or
 * the section's `data-status` (no results, or the count did not come). A failed search is logged; if another query's
 * results are on screen, the pop-up goes back to idle rather than pass them off as this query's.
 * Nodes are swapped only after a fetch, never during the click that started it: dialog.js closes the dialog when a
 * click's target is no longer inside it.
 * - The query is also remembered on a submit, and when a result card or "View all" is opened (the query whose results
 *   are on screen).
 *
 * Focus and closing: dialog.js hears Escape only as a keydown inside the component, and unlocks the page (body
 * `position: fixed`, the scroll position) only in its own closeDialog(). So the focus never stays on <body>: when a
 * swap or a hidden block (results in, a recent row, Recent's Clear) takes the focused element away, the search box
 * gets the focus after keyboard use, the body (`tabindex="-1"`, no ring) after a click or tap, so phones keep their
 * keyboard down. And the browser's own close request (Escape with the focus outside, Android's back gesture) goes
 * through closeDialog() when it can be cancelled; a close that skipped closeDialog() still unlocks the page.
 */
(() => {
  const STORAGE_KEY = 'fd-search-recent';
  const MAX_RECENT = 4;
  const MAX_LENGTH = 80;
  const PHONE = '(max-width: 749px)';
  const DEBOUNCE = 250;

  /**
   * Cleans a stored list: strings only, trimmed and cut to MAX_LENGTH, no blanks, no case-insensitive duplicates,
   * at most MAX_RECENT.
   * @param {unknown} list
   * @returns {string[]}
   */
  const normalise = (list) => {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const out = [];
    for (const item of list) {
      if (typeof item !== 'string') continue;
      const query = item.trim().slice(0, MAX_LENGTH).trim();
      const key = query.toLowerCase();
      if (!query || seen.has(key)) continue;
      seen.add(key);
      out.push(query);
      if (out.length === MAX_RECENT) break;
    }
    return out;
  };

  /** @returns {string[]} The recent searches, or none when storage is blocked or unreadable. */
  const readRecent = () => {
    try {
      return normalise(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]'));
    } catch (error) {
      return [];
    }
  };

  /** @param {string[]} list */
  const writeRecent = (list) => {
    try {
      if (list.length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Storage blocked or full: recent searches are a convenience only.
    }
  };

  class FdSearch extends HTMLElement {
    /** @type {AbortController | null} */
    #listeners = null;

    /** @type {HTMLInputElement | null} */
    #input = null;

    /** @type {HTMLButtonElement | null} */
    #clearButton = null;

    /** @type {string | null} The full placeholder (desktop), kept from the first connection. */
    #placeholder = null;

    /** @type {HTMLElement | null} */
    #results = null;

    /** @type {HTMLElement | null} */
    #chips = null;

    /** @type {HTMLElement | null} The scrolling body: where the focus goes after a click took it away. */
    #body = null;

    /** Whether the header shows the popular chips (until a search brings its own suggestions). */
    #popular = true;

    /** @type {number | undefined} The typing pause. */
    #timer;

    /** @type {AbortController | null} The current search's requests. */
    #request = null;

    /** @type {string | null} The query on screen or on its way: '' when idle, null after a failed search. */
    #query = '';

    /** The query whose results are on screen. */
    #shown = '';

    connectedCallback() {
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector('#FdSearchInput'));
      if (!input) return;
      this.#input = input;
      this.#clearButton = this.querySelector('.fd-search__clear');
      this.#results = this.querySelector('.fd-search__results');
      this.#chips = this.querySelector('.fd-search__chips');
      this.#body = this.querySelector('.fd-search__body');

      this.#listeners = new AbortController();
      const { signal } = this.#listeners;

      input.addEventListener('input', this.#onInput, { signal });
      this.querySelector('.fd-search__form')?.addEventListener('submit', this.#onSubmit, { signal });
      this.addEventListener('click', this.#onClick, { signal });

      const dialog = this.closest('dialog');
      dialog?.addEventListener('cancel', this.#onCancel, { signal });
      dialog?.addEventListener('close', this.#onClose, { signal });

      // Phones get the shorter placeholder.
      const phone = window.matchMedia(PHONE);
      const long = (this.#placeholder ??= input.placeholder);
      const short = input.dataset.placeholderShort || long;
      const placeholder = () => {
        input.placeholder = phone.matches ? short : long;
      };
      phone.addEventListener('change', placeholder, { signal });
      placeholder();

      // Recent searches may have changed elsewhere (another tab, a search page) since the last opening.
      this.closest('dialog-component')?.addEventListener('dialog:open', () => this.#renderRecent(), { signal });

      this.#syncClear();
      this.#renderRecent();
    }

    disconnectedCallback() {
      this.#listeners?.abort();
      this.#listeners = null;
      window.clearTimeout(this.#timer);
      if (this.#request) {
        this.#request.abort();
        this.#request = null;
        this.#query = null;
      }
    }

    /**
     * Searches for `query` in the pop-up: fills the box (unless it already holds that query) and shows the results
     * when they arrive (the current view stays until then). An empty query goes back to the idle state.
     * @param {string} query
     */
    run(query) {
      if (!this.#input) return;
      const q = String(query ?? '').trim();
      if (this.#input.value.trim() !== q) this.#input.value = q;
      this.#syncClear();
      window.clearTimeout(this.#timer);
      if (q === this.#query) return;
      this.#query = q;
      this.#request?.abort();
      this.#request = null;
      if (q) this.#search(q);
      else this.#reset();
    }

    /**
     * Records `query` as the newest recent search. The list on screen is redrawn the next time the idle state shows
     * (redrawing now would detach a clicked recent row, which dialog.js would take for a click outside).
     * @param {string} query
     */
    remember(query) {
      const q = String(query ?? '');
      if (!q.trim()) return;
      writeRecent(normalise([q, ...readRecent()]));
    }

    /** Forgets every recent search and hides the block. */
    clearRecent() {
      const focus = this.#focusBefore();
      writeRecent([]);
      this.#renderRecent();
      this.#keepFocus(focus);
    }

    /** @param {'idle' | 'results' | 'empty'} state */
    setState(state) {
      this.dataset.state = state;
      if (state === 'idle') this.#renderRecent();
    }

    /**
     * The browser's own close request (Escape with the focus outside the component, Android's back gesture): closed the
     * way dialog.js does, with the closing animation and the page unlocked. Chrome lets it be cancelled only after a
     * fresh user action; otherwise the dialog closes at once and #onClose unlocks the page.
     * @param {Event} event
     */
    #onCancel = (event) => {
      const component = /** @type {(HTMLElement & { closeDialog?: () => void }) | null} */ (this.closest('dialog-component'));
      if (typeof component?.closeDialog !== 'function') return;
      event.preventDefault();
      // Already closing (a second Escape during the animation): let that close finish.
      if (!(/** @type {HTMLDialogElement} */ (event.currentTarget).classList.contains('dialog-closing'))) component.closeDialog();
    };

    /**
     * Unlocks the page after a close that skipped dialog.js's closeDialog(), which unlocks it before it closes the
     * dialog (so after its own close the body is no longer fixed): the body styles go, and the page goes back to the
     * scroll position the lock kept in `top`.
     */
    #onClose = () => {
      if (this.closest('dialog')?.open) return;
      const { style } = document.body;
      if (style.position !== 'fixed') return;
      const top = -Number.parseFloat(style.top) || 0;
      style.width = '';
      style.position = '';
      style.top = '';
      window.scrollTo({ top, behavior: 'instant' });
    };

    #onInput = () => {
      this.#syncClear();
      window.clearTimeout(this.#timer);
      if (!this.#input?.value.trim()) {
        this.run('');
        return;
      }
      this.#timer = window.setTimeout(() => this.run(this.#input?.value ?? ''), DEBOUNCE);
    };

    /** @param {SubmitEvent} event */
    #onSubmit = (event) => {
      const q = this.#input?.value.trim() ?? '';
      if (!q) {
        event.preventDefault();
        this.#input?.focus();
        return;
      }
      if (this.#input) this.#input.value = q;
      window.clearTimeout(this.#timer);
      this.remember(q);
    };

    /** @param {MouseEvent} event */
    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('.fd-search__close')) {
        /** @type {(HTMLElement & { closeDialog?: () => void }) | null} */ (this.closest('dialog-component'))?.closeDialog?.();
      } else if (target.closest('.fd-search__clear')) {
        this.run('');
        this.#input?.focus();
      } else if (target.closest('.fd-search__recent-clear')) {
        this.clearRecent();
      } else if (target.closest('.fd-search__results :is(a.fd-search-card, a.fd-search-results__all)')) {
        // A result opens (the link goes on): remember the query that found it.
        this.remember(this.#shown);
      } else {
        const pick = /** @type {HTMLElement | null} */ (target.closest('button[data-q]'));
        const q = pick?.dataset.q?.trim();
        if (!q) return;
        this.remember(q);
        this.run(q);
      }
    };

    /**
     * Fetches the results (and the count) for `q` and shows them, unless another search has started meanwhile.
     * @param {string} q
     */
    async #search(q) {
      const request = new AbortController();
      this.#request = request;
      const { signal } = request;
      const current = () => !signal.aborted && this.#query === q;
      const predictive = this.dataset.predictive !== 'false';
      const params = new URLSearchParams({ q });
      if (predictive) {
        params.set('resources[type]', 'product,collection,query');
        params.set('resources[limit]', '10');
        params.set('resources[limit_scope]', 'each');
      } else {
        params.set('type', 'product');
        params.set('options[prefix]', 'last');
      }
      params.set('section_id', 'fd-search-results');
      const base = (predictive ? this.dataset.resultsUrl : this.dataset.searchUrl) || '';
      // The count runs alongside; without it "View all results" just has no number.
      const count = predictive ? this.#fetchCount(q, signal).catch(() => null) : null;

      try {
        const response = await fetch(`${base}?${params}`, { signal });
        if (!response.ok) throw new Error(`Search results: HTTP ${response.status}`);
        const html = await response.text();
        if (!current()) return;
        const root = /** @type {HTMLElement | null} */ (
          new DOMParser().parseFromString(html, 'text/html').querySelector('.fd-search-results')
        );
        if (!root) throw new Error('Search results: no .fd-search-results in the response');
        this.#show(root, q);

        // The live region: no results at once; results once with the real count (the store search answer has it,
        // predictive search waits for the count request), or with the number of cards if the count does not come.
        const status = root.dataset.status || '';
        if (root.dataset.state !== 'results') {
          this.#announce(status);
          return;
        }
        const total = count ? await count : { count: Number(root.dataset.count), status };
        if (!current()) return;
        if (!(total && this.#showCount({ count: total.count, status: total.status || status }))) this.#announce(status);
      } catch (error) {
        if (signal.aborted) return;
        console.error('fd-search: the search for', JSON.stringify(q), 'failed.', error);
        // Never leave another query's results looking like the answer to this one.
        if (this.#shown !== q) this.#reset();
        // Let the same query be tried again (next keystroke or click).
        if (this.#query === q) this.#query = null;
      } finally {
        if (this.#request === request) this.#request = null;
      }
    }

    /**
     * The store search's result count for `q` (sections/fd-search-count.liquid) and its live-region text.
     * @param {string} q
     * @param {AbortSignal} signal
     * @returns {Promise<{ count: number, status: string } | null>}
     */
    async #fetchCount(q, signal) {
      const params = new URLSearchParams({ q, type: 'product', 'options[prefix]': 'last', section_id: 'fd-search-count' });
      const response = await fetch(`${this.dataset.searchUrl || ''}?${params}`, { signal });
      if (!response.ok) return null;
      const node = /** @type {HTMLElement | null} */ (
        new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-fd-search-count]')
      );
      const total = Number.parseInt(node?.textContent ?? '', 10);
      return node && Number.isFinite(total) ? { count: total, status: node.dataset.status || '' } : null;
    }

    /**
     * Puts a results section on screen, with its chips in the header, its status in the live region and its state.
     * @param {HTMLElement} root
     * @param {string} q
     */
    #show(root, q) {
      // The chip, suggestion or recent row that started this search is about to be replaced or hidden.
      const focus = this.#focusBefore();
      const template = /** @type {HTMLTemplateElement | null} */ (root.querySelector('template[data-fd-search-chips]'));
      template?.remove();
      this.#setChips(template && template.content.childElementCount > 0 ? template.content : null);
      this.#announce('');
      this.#results?.replaceChildren(root);
      this.#shown = q;
      this.setState(root.dataset.state === 'empty' ? 'empty' : 'results');
      // New results start at the top, wherever the previous ones were scrolled to.
      if (this.#results?.parentElement) this.#results.parentElement.scrollTop = 0;
      this.#keepFocus(focus);
    }

    /** Back to the idle state (the box keeps its text): popular chips, no results, nothing announced. */
    #reset() {
      const focus = this.#focusBefore();
      this.#shown = '';
      this.setState('idle');
      this.#setChips(null);
      this.#results?.replaceChildren();
      this.#announce('');
      this.#keepFocus(focus);
    }

    /**
     * Where the focus is, before a change that may remove or hide it (see #keepFocus).
     * @returns {{ element: HTMLElement | null, keyboard: boolean }}
     */
    #focusBefore() {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !this.contains(active)) return { element: null, keyboard: false };
      return { element: active, keyboard: active.matches(':focus-visible') };
    }

    /**
     * Keeps the focus in the open pop-up when the change took it away (the element is gone or no longer drawn, or the
     * focus was already on <body>, as after a click in Safari, which does not focus buttons): the search box after
     * keyboard use, else the body without scrolling, so a phone does not raise its keyboard.
     * @param {{ element: HTMLElement | null, keyboard: boolean }} before
     */
    #keepFocus({ element, keyboard }) {
      if (!this.closest('dialog')?.open) return;
      const active = document.activeElement;
      const lost = element
        ? !element.isConnected || element.getClientRects().length === 0
        : !active || active === document.body;
      if (!lost) return;
      if (keyboard) this.#input?.focus();
      else this.#body?.focus({ preventScroll: true });
    }

    /**
     * Fills "N products" and "View all N results" and announces the count, when the results are still on screen.
     * @param {{ count: number, status: string }} total
     * @returns {boolean} Whether the count was shown.
     */
    #showCount({ count, status }) {
      if (!Number.isFinite(count) || count <= 0) return false;
      const root = /** @type {HTMLElement | null | undefined} */ (this.#results?.querySelector('.fd-search-results'));
      if (!root || root.dataset.state !== 'results') return false;
      const products = /** @type {HTMLElement | null | undefined} */ (root.querySelector('.fd-search-results__total'));
      const label = root.querySelector('[data-fd-search-all-label]');
      let rule = 'other';
      try {
        rule = new Intl.PluralRules(document.documentElement.lang || undefined).select(count);
      } catch (error) {
        // An unknown language tag: the "other" form.
      }
      const pattern = (rule === 'one' && this.dataset.countOne) || this.dataset.countOther;
      if (products && pattern) {
        products.textContent = pattern.replace('[count]', String(count));
        products.hidden = false;
      }
      if (label && this.dataset.viewAllCount) label.textContent = this.dataset.viewAllCount.replace('[count]', String(count));
      this.#announce(status);
      return true;
    }

    /**
     * Header chips: a search's suggestions, or the popular searches (`null`).
     * @param {DocumentFragment | null} content
     */
    #setChips(content) {
      if (!this.#chips) return;
      if (content) {
        this.#chips.replaceChildren(document.importNode(content, true));
        this.#popular = false;
        return;
      }
      if (this.#popular) return;
      const popular = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-fd-search-popular]'));
      this.#chips.replaceChildren(...(popular ? [document.importNode(popular.content, true)] : []));
      this.#popular = true;
    }

    /** @param {string} text The live region's text (the result count). */
    #announce(text) {
      const status = this.querySelector('.fd-search__status');
      if (status) status.textContent = text;
    }

    /** Shows the Clear button only while the box has text. */
    #syncClear() {
      if (this.#clearButton && this.#input) this.#clearButton.hidden = this.#input.value === '';
    }

    /** Draws the recent list from storage; the block hides when there is nothing to show. */
    #renderRecent() {
      const block = /** @type {HTMLElement | null} */ (this.querySelector('.fd-search__recent'));
      const list = block?.querySelector('.fd-search__recent-list');
      if (!block || !list) return;
      const items = readRecent();
      list.replaceChildren(
        ...items.map((query) => {
          const row = document.createElement('li');
          const button = document.createElement('button');
          const text = document.createElement('span');
          button.type = 'button';
          button.className = 'fd-search__recent-item';
          button.dataset.q = query;
          text.className = 'fd-search__recent-text';
          text.textContent = query;
          button.append(text);
          row.append(button);
          return row;
        })
      );
      block.hidden = items.length === 0;
    }
  }

  if (!customElements.get('fd-search')) customElements.define('fd-search', FdSearch);
})();
